import 'twin.macro';
import 'styled-components/macro';

import { useState } from 'react';

import { InfoIcon, Switch } from 'component-lib';
import { Select } from 'component-lib/atoms/select/Select';
import { SelectItem } from 'component-lib/atoms/select/SelectItem';
import { ROLES, ROLE_LABEL_BY_VALUE } from 'create/atoms/authorization/Roles';
import { useAuthUserContext } from 'support/auth/AuthUserContext';
import { useLocalStorage } from 'support/react/hooks';
import { RoleName } from 'types/graphql';

import { flags } from './flags';
import { ReactComponent as ArrowLeft } from './images/arrow-left.svg';
import { ReactComponent as ArrowRight } from './images/arrow-right.svg';

export type Flag = keyof typeof flags;

export const FeatureFlagManager = () => {
  const { instruqtUser } = useAuthUserContext();
  const [showFeatureManager] = useLocalStorage('feature_manager', { initialValue: true });
  const [open, setOpen] = useState(false);

  // Can't check is_global_admin as the role override will modify that
  if (!showFeatureManager || !instruqtUser.teams.some(edge => edge.node?.slug === 'instruqt')) {
    return null;
  }

  return (
    <div tw="fixed bottom-[30px] right-0 flex grow-0 items-end z-overlay">
      <button tw="p-1 bg-primary text-white relative bottom-[10px] rounded-l-lg" onClick={() => setOpen(open => !open)}>
        {open ? <ArrowRight width={15} height={15} /> : <ArrowLeft width={15} height={15} />}
      </button>
      {open && <FeatureFlags />}
    </div>
  );
};

const FeatureFlags = () => {
  const [role, setRole] = useOverrideRole();
  const flagList = Object.entries(flags) as [key: Flag, title: string][];

  return (
    <div tw="bg-white p-2 w-[300px] flex flex-col gap-2 border-[1px] border-primary border-r-0 rounded-l-lg">
      <div tw="flex flex-col gap-1">
        <h3 tw="display-6 flex items-center gap-1">
          Role impersonation <InfoIcon>This won't affect backend permissions, only frontend rendering</InfoIcon>
        </h3>

        <Select<RoleName | ''>
          onValueChange={setRole}
          value={role || ''}
          triggerElement={role ? ROLE_LABEL_BY_VALUE[role] : 'No override'}
        >
          <SelectItem value="">No override</SelectItem>

          {ROLES.map(({ label, value }) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </Select>
      </div>

      <div tw="flex flex-col gap-1">
        <h3 tw="display-6">Feature flags</h3>
        {flagList.length ? (
          flagList.map(([key, title]): React.ReactElement => <FeatureFlag key={key as Flag} name={key} title={title} />)
        ) : (
          <h3 tw="text-gray-50">No feature flag has been found</h3>
        )}
      </div>
    </div>
  );
};

/**
 * Use this hook to get the status of feature flag through the app
 *
 * @param name Name of the feature flag
 */
export const useFeatureFlag = (name: Flag) => useLocalStorage(name, { initialValue: false });
export const useOverrideRole = () => useLocalStorage<RoleName | ''>('_override_role', { initialValue: '' });

const FeatureFlag = ({ name, title }: { name: Flag; title: string }) => {
  const [isFeatureActive, setIsFeatureActive] = useFeatureFlag(name);

  return (
    <Switch value={isFeatureActive} onChange={() => setIsFeatureActive(!isFeatureActive)}>
      {title}
    </Switch>
  );
};
